"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.emptyOrderTotals = exports.TicketTypeUtils = void 0;
exports.calculateOrderTotals = calculateOrderTotals;
exports.priceTicketTypes = priceTicketTypes;
const decimal_js_1 = require("decimal.js");
const numbers_1 = require("./numbers");
const currency_1 = require("./currency");
const fees_1 = require("./fees");
class TicketTypeUtils {
    constructor(type, event, organization, quantity = 1, itemCount = 1) {
        this.taxRate = new decimal_js_1.Decimal(0);
        this.type = type;
        this.event = event;
        this.organization = organization;
        this.quantity = quantity;
        this.itemCount = itemCount;
    }
    /**
     * The selected location's sales tax percentage. Held on the instance so
     * {@link allInPrice} and {@link priceDisplayDetail} agree.
     */
    withTaxRate(taxRate) {
        this.taxRate = taxRate;
        return this;
    }
    /** "$5.00 ticket + $1.00 service fee" beneath the advertised price; null when nothing is added. */
    priceDisplayDetail() {
        const serviceFee = this.serviceFee();
        if (serviceFee === 0) {
            return null;
        }
        return `${(0, currency_1.formatCurrency)(this.base())} ticket + ${(0, currency_1.formatCurrency)(serviceFee)} ${TicketTypeUtils.SERVICE_FEE_DESCRIPTION}`;
    }
    base() {
        const base = this.type.base ?? 0;
        return base * this.quantity;
    }
    /** This ticket's share of the processor's cut of the taxed total, rounded up. See {@link reversePayment}. */
    processing() {
        return this.payment().processing;
    }
    /** Free tickets and absorbed processing short-circuit before the rate is consulted. */
    payment() {
        const amount = this.totalCost();
        if (amount === 0 || !this.event.collect_processor_fees) {
            return (0, fees_1.reversePayment)(amount, 0, this.taxRate);
        }
        if (typeof this.organization.rate_credit !== 'number') {
            throw new Error('Organization does not have a credit rate defined; cannot determine processing fee');
        }
        return (0, fees_1.reversePayment)(amount, this.organization.rate_credit, this.taxRate);
    }
    /** Collected platform fee plus this ticket's processing share. */
    serviceFee() {
        return this.collectedFee() + this.processing();
    }
    /** The advertised price: base plus every fee. Tax stays a Summary line but feeds the gross-up. */
    allInPrice() {
        return this.payment().subtotal;
    }
    fee() {
        if (this.type.is_coach_pass) {
            return 0;
        }
        // A free ticket never carries a platform fee, whatever the org's billing
        // shape. The flat per-ticket and tier branches below both ignore `base`,
        // so without this a $0 type reports the org's per-ticket fee (ENG-4176).
        // Mirrors the guard in processing().
        if (this.base() === 0) {
            return 0;
        }
        let fee;
        const perTicketFee = this.organization.platform_per_ticket_fee ?? 0;
        const percentage = new decimal_js_1.Decimal(this.organization.platform_percentage_ticketing ?? 0);
        const minimum = this.organization.platform_percentage_minimum ?? 0;
        const base = this.type.base ?? 0;
        if (perTicketFee > 0) {
            fee = perTicketFee * this.quantity;
        }
        else if (percentage.gt(0)) {
            fee = (0, numbers_1.calculateFromRate)(base * this.quantity, percentage, minimum);
        }
        else {
            fee = +(this.type.ticket_tier?.fee_per ?? 0) * this.quantity;
        }
        return fee;
    }
    /** The platform fee passed on to the buyer; zero when absorbed. */
    collectedFee() {
        return this.event.collect_platform_fees ? this.fee() : 0;
    }
    totalCost() {
        return this.base() + this.collectedFee();
    }
    static filterUnavailable(types, filterZeroBase = false) {
        return types.filter((type) => {
            if (!TicketTypeUtils.available(type)) {
                return false;
            }
            // Because we're not all computers and this is way more readable, ignore the warning
            // noinspection RedundantIfStatementJS
            if (filterZeroBase && !type.base) {
                return false;
            }
            return true;
        });
    }
    static available(type) {
        const now = new Date();
        return ((!type.available_at || new Date(type.available_at) <= now) &&
            (!type.available_until || new Date(type.available_until) >= now));
    }
}
exports.TicketTypeUtils = TicketTypeUtils;
TicketTypeUtils.SERVICE_FEE_DESCRIPTION = 'service fee';
const emptyOrderTotals = () => ({
    lines: [],
    base: 0,
    platformFee: 0,
    collectedPlatformFee: 0,
    processingFee: 0,
    subtotal: 0,
    tax: 0,
    total: 0,
});
exports.emptyOrderTotals = emptyOrderTotals;
/**
 * Prices a cart from each type's advertised price — the listing's price is
 * a promise — and taxes the subtotal once. That is never more than the
 * per-ticket tax each share was sized against, so the shares still cover
 * it. See docs/ticketing/fees.md.
 *
 * @param taxRate the selected location's sales tax percentage
 */
function calculateOrderTotals(lines, event, organization, taxRate) {
    if (lines.length === 0) {
        return (0, exports.emptyOrderTotals)();
    }
    if (!organization) {
        throw new Error('Event must have an organization to calculate ticket fees');
    }
    const totals = (0, exports.emptyOrderTotals)();
    for (const { ticketType, quantity } of lines) {
        const utils = new TicketTypeUtils(ticketType, event, organization).withTaxRate(taxRate);
        const unitPrice = utils.allInPrice();
        totals.lines.push({ ticketType, quantity, unitPrice, amount: unitPrice * quantity });
        totals.base += utils.base() * quantity;
        totals.platformFee += utils.fee() * quantity;
        totals.processingFee += utils.processing() * quantity;
        totals.subtotal += unitPrice * quantity;
    }
    totals.collectedPlatformFee = event.collect_platform_fees ? totals.platformFee : 0;
    totals.tax = (0, numbers_1.calculateFromRate)(totals.subtotal, taxRate);
    totals.total = totals.subtotal + totals.tax;
    return totals;
}
/**
 * Prices a listing at the advertised all-in price (docs/ticketing/fees.md).
 * Without an event or organization (still loading, or absent) there are no
 * fee flags or rates, so the base price stands in rather than a throw.
 *
 * @param taxRate the selected location's sales tax percentage
 */
function priceTicketTypes(types, event, organization, taxRate) {
    return types.map((ticketType) => {
        const utils = event && organization
            ? new TicketTypeUtils(ticketType, event, organization).withTaxRate(taxRate)
            : null;
        return {
            ticketType,
            price: utils?.allInPrice() ?? ticketType.base ?? 0,
            detail: utils?.priceDisplayDetail() ?? null,
        };
    });
}
